import React from "react";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import Utils from "../../utils.js";
import FeatherIcon from "feather-icons-react";
import { AuthContext } from "../../components/auth/AuthContext.js";
import { useSnackbar } from "../../components/toast/index.jsx";
import SettingHeader from "./setting-header.jsx";
import CreateUserComponent from "./create-user.jsx";
import { Grid } from "@mui/material";
import StatusChangeSwitch from "./status-change-switch.jsx";
import ChangeAdminSwitch from "./change-admin-switch.jsx";
import { COMPANY_UPDATED_FAILED, COMPANY_UPDATED_SUCCESS } from "../../helpers/messages.js";
import { addUserToGroupAPI, removeUserToGroupAPI } from "../../services/group.js";
import TableComponent from "../../components/table/list.jsx";
import { ADMIN_GROUP_NAME, LIST_NULL_OPTION_VALUE } from "../../helpers/constant.js";
import CommonSelectComponent from "../../components/table/filters/common-select.jsx";
import { getAdminUsersListAPI, getCompanyListAPI } from "../../services/settings.js";

function AdminUser() {
    let navigate = useNavigate();
    const { setUserGroups, user, isAdmin, isAuthenticated } = React.useContext(AuthContext); // Extract setUserGroups from AuthContext
    const [data, setData] = React.useState([]);
    const [companyList, setCompanyList] = React.useState([]);
    const [newCampDiag, setNewCampDiag] = React.useState(false);
    const [selectedTab] = React.useState("User setup");
    const [spinner, setSpinner] = React.useState(false);
    const showSnackbar = useSnackbar();
    const [updatingUserId, setUpdatingUserId] = React.useState(null);
    const [pageInfo, setPageInfo] = React.useState({
        currentPage: 0,
        rowsPerPage: 10,
        nextPage: null,
        groupName: LIST_NULL_OPTION_VALUE
    });
    const [totalRecords, setTotalRecords] = React.useState(0);

    async function getData(rowsPerPage = pageInfo.rowsPerPage, nextPage = pageInfo.nextPage, groupName = pageInfo.groupName) {
        try {
            setSpinner(true);
            const _data = await getAdminUsersListAPI({
                limit: rowsPerPage,
                lastKey: nextPage,
                groupName: groupName === LIST_NULL_OPTION_VALUE ? null : groupName,
            });
            let newData = [..._data.items];
            if (nextPage) {
                newData = [...data, ..._data.items];
            }
            newData.forEach((item) => {
                item.admin = item.group.includes(ADMIN_GROUP_NAME)
                item.company = item.group.filter(i => i !== ADMIN_GROUP_NAME)[0] || null;
            });
            setData([...newData]);
            setTotalRecords(_data.totalRecords);
            setPageInfo((prev) => ({ ...prev, nextPage: _data.lastKey || null }));
            setSpinner(false); // Update the state with all items
        } catch (error) {
            console.error("Error fetching campaign _data: ", error);
            setSpinner(false);
        }
    }

    async function getCompanyData() {
        try {
            let { items } = await getCompanyListAPI({ limit: 1000, lastKey: null });
            setCompanyList(items);
        } catch (error) {
            console.error("Error fetching company data: ", error);
        }
    }

    React.useEffect(() => {
        if (isAuthenticated) {
            if (!isAdmin) {
                navigate("/");
                return;
            }
            getCompanyData();
            getData();
        }
        window.scrollTo(0, 0); // Scroll to top when component mounts
    }, [isAdmin, isAuthenticated]);

    // Method to update the user status.
    const userUpdateCallback = (_user, key) => {
        const _data = [...data];
        _data.forEach((u) => {
            if (u.username === _user.username) {
                u[key] = !_user[key];
            }
        });
        setData([..._data])
    }

    // on change user company
    const onChangeCompanyHandler = async (value, item) => {
        try {
            const adminSelected = value === ADMIN_GROUP_NAME;
            if (item.admin && adminSelected) {
                return;
            }
            setUpdatingUserId(item.username);
            if (item.group.length !== 0) {
                await removeUserToGroupAPI({
                    GroupName: Array.isArray(item.company) ? item.company[0] : item.company,
                    Username: item.username,
                });
            }
            await addUserToGroupAPI({
                GroupName: value,
                Username: item.username
            });

            // update the group in the data state
            const updateIndex = data.findIndex((u) => u.username === item.username);
            if (updateIndex !== -1) {
                const group = [];
                if (value === ADMIN_GROUP_NAME) {
                    group.push(ADMIN_GROUP_NAME);
                    data[updateIndex].admin = true;
                } else {
                    group.push(value);
                    data[updateIndex].company = value;
                }
                data[updateIndex].group = group;
            }

            setData([...data]);
            showSnackbar(COMPANY_UPDATED_SUCCESS);
            setUpdatingUserId(null);

            const updatedGroups = await Utils.refreshUserGroups();
            setUserGroups(updatedGroups); // Update AuthContext here
        } catch (error) {
            setUpdatingUserId(null);
            showSnackbar(COMPANY_UPDATED_FAILED, "error");
        }
    }

    // define table headers
    const columns = React.useMemo(() => ([
        {
            label: "User",
            key: "fullName",
            sortable: true,
            style: { width: "15%" },
            renderItem: (row) => row.fullName || "-",
        },
        {
            label: "Email",
            key: "email",
            sortable: true,
            renderItem: (row) => row.email || "-",
        },
        {
            label: "Admin",
            key: "admin",
            sortable: true,
            renderItem: (row) => (<ChangeAdminSwitch checked={row.admin} user={row} onSuccess={() => userUpdateCallback(row, 'admin')} />)
        },
        {
            label: "Status",
            key: "enabled",
            sortable: true,
            renderItem: (row) => (<StatusChangeSwitch checked={row.enabled} user={row} disabled={row.username === user.username} onSuccess={() => userUpdateCallback(row, 'enabled')} />)
        },
        // {
        //     label: "Role",
        //     key: "role",
        //     sortable: true,
        //     renderItem: (row) => row.role || "-",
        // },
        {
            label: "Company",
            key: "company",
            sortable: true,
            renderItem: (row, rowIndex, columnIndex) => (<CommonSelectComponent
                htmlId={`select-${rowIndex}-${columnIndex}`}
                label="Company"
                labelKey={"name"}
                valueKey={"internalname"}
                Placeholder={"Select Company"}
                size="small"
                spinner={spinner}
                filterOptions={companyList}
                selected={row.company}
                isLoading={updatingUserId === row.username}
                setSelected={(value) => onChangeCompanyHandler(value, row)}
            />)
        }
    ]), [data, spinner, updatingUserId, companyList]);

    const headerActionButtons = () => {
        return (
            <Button
                variant="contained"
                onClick={() => setNewCampDiag(true)}
                startIcon={<FeatherIcon icon="plus-circle" size={20} />}
            >
                Create User
            </Button>
        )
    }

    const handlePageChange = (_pageInfo) => {
        let nextPage = pageInfo.nextPage;
        if (_pageInfo.REFRESH_DATA) {
            nextPage = null
        }
        setPageInfo({
            ...pageInfo,
            nextPage,
            currentPage: _pageInfo.currentPage,
            rowsPerPage: _pageInfo.rowsPerPage
        });
        if (nextPage || _pageInfo.REFRESH_DATA) {
            getData(_pageInfo.rowsPerPage, nextPage);
        }
    }

    const handleFilter = (filterData) => {
        const newPageInfo = {
            ...pageInfo,
            currentPage: 0,
            nextPage: null,
            groupName: filterData.group || LIST_NULL_OPTION_VALUE,
        };
        setPageInfo(newPageInfo);
        getData(newPageInfo.rowsPerPage, null, filterData.group);
    }

    return (
        <Grid display="flex" flexDirection="column" flexGrow="1" overflow="auto">
            <SettingHeader title={"Users"} selectedTab={selectedTab} />
            <Grid
                className="vocalysed-home-upadted setting-user-setup"
                container
                spacing={0}
                direction="column"
                flexWrap="nowrap"
                flexGrow={1}
                paddingTop={2}
                paddingX={3}
                paddingBottom={3}
                overflow="auto"
            >
                <TableComponent
                    headerActionButtons={headerActionButtons}
                    spinner={spinner}
                    data={data}
                    columns={columns}
                    pagination={true}
                    serverPaginate={true}
                    currentPage={pageInfo.currentPage}
                    hasNextPage={pageInfo.nextPage !== null}
                    totalCount={totalRecords}
                    searchKeys={["fullName", "email", "company"]}
                    filterTypes={{ groupName: "group" }}
                    defaultFilterValues={{ groupName: pageInfo.groupName || LIST_NULL_OPTION_VALUE }}
                    noDataMessage={"No users found."}
                    companyListOptions={companyList}
                    onPageChange={handlePageChange}
                    onFilter={handleFilter}
                />
            </Grid>

            {/* Create User Modal */}
            <CreateUserComponent
                open={newCampDiag}
                companyList={companyList}
                onClose={async (inp) => {
                    if (inp === null) setNewCampDiag(false);
                    else {
                        handlePageChange({ REFRESH_DATA: true, currentPage: 0, rowsPerPage: pageInfo.rowsPerPage });
                        setNewCampDiag(false);
                    }
                }}
            />
        </Grid>
    );
}

export default AdminUser;
